import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Field, Input, Modal, Select, Textarea } from "./common";
import { typeStyle } from "../utils/calendarEvents";
import { toDateInput, toLocalInput } from "../utils/calendarDates";

const TYPES = ["task", "call", "email", "meeting", "note"];

/**
 * Create/edit form for one calendar entry. Follows the Activities page's modal
 * (same field vocabulary, same Select/Input components) rather than inventing a
 * second activity editor -- with two additions the calendar needs: a time, and
 * an end time for meetings.
 *
 * A `meeting` type routes through the meetings API, not the activities one: a
 * Meeting is the extension row that carries `ends_at`, and its router is what
 * keeps Activity.due_date in sync with starts_at. Creating a bare
 * Activity(type="meeting") here would produce a meeting the Meetings page
 * can't see and whose end time has nowhere to live.
 */
export default function CalendarEventModal({
  open, draft, contacts, saving, error, onChange, onClose, onSubmit,
}) {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);

  useEffect(() => { if (open) setTouched(false); }, [open]);

  const isMeeting = draft?.type === "meeting";
  // The type is fixed once a Meeting extension row exists: its API can't
  // express "this is a task now", and rewriting the anchor activity directly
  // would leave the meeting (with its transcript and extraction) orphaned
  // behind a task. Converting one is a job for the meeting page.
  const lockType = !!draft?.meetingId;
  // An end time only has somewhere to live when the save goes through the
  // meetings API -- a plain Activity has a due_date and no end.
  const canSetEnd = isMeeting && (!draft?.id || !!draft?.meetingId);
  const subjectMissing = !draft?.subject?.trim();
  const timeInvalid = useMemo(() => {
    if (!draft || draft.allDay || !canSetEnd || !draft.endTime || !draft.startTime) return false;
    return draft.endTime <= draft.startTime;
  }, [draft, canSetEnd]);

  if (!draft) return null;
  const set = (key) => (e) => onChange({ ...draft, [key]: e.target.value });

  const submit = () => {
    setTouched(true);
    if (subjectMissing || timeInvalid) return;
    onSubmit();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={draft.id ? t("calendar.editEvent") : t("calendar.newEvent")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="cal-save-event">
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </>
      }
    >
      {error && (
        <p role="alert" className="text-sm text-danger border border-danger/40 bg-danger/10 rounded-sm px-3 py-2">
          {error}
        </p>
      )}

      {/* Not <Field>: that renders a <label>, and a label wrapping five buttons
          is neither valid nor useful. */}
      <div className="space-y-1.5">
        <span className="block text-xs uppercase tracking-[0.15em] text-muted">{t("activity.type")}</span>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t("activity.type")}>
          {TYPES.map((type) => {
            const style = typeStyle(type);
            const active = draft.type === type;
            return (
              <button
                key={type}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={lockType && !active}
                onClick={() => onChange({ ...draft, type })}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs font-medium outline-none
                  transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/60
                  disabled:opacity-40 disabled:cursor-not-allowed
                  ${active ? style.tint : "border-border text-muted enabled:hover:bg-surface enabled:hover:text-txt"}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${active ? style.dot : "bg-muted opacity-40"}`} />
                {t(`statuses.${type}`)}
              </button>
            );
          })}
        </div>
        {lockType && <span className="block text-xs text-muted">{t("calendar.typeLocked")}</span>}
      </div>

      <Field label={t("activity.subject")}>
        <Input
          data-testid="cal-event-subject"
          autoFocus
          value={draft.subject}
          onChange={set("subject")}
          aria-invalid={touched && subjectMissing}
        />
        {touched && subjectMissing && (
          <span className="text-xs text-danger">{t("calendar.subjectRequired")}</span>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t("activity.dueDate")}>
          <Input type="date" value={draft.date} onChange={set("date")} data-testid="cal-event-date" />
        </Field>
        <Field label={t("calendar.startTime")}>
          <Input
            type="time"
            step={300}
            value={draft.allDay ? "" : draft.startTime}
            disabled={draft.allDay}
            onChange={set("startTime")}
            data-testid="cal-event-time"
          />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
        <input
          type="checkbox"
          checked={draft.allDay}
          onChange={(e) => onChange({ ...draft, allDay: e.target.checked })}
          className="w-4 h-4 rounded-sm accent-primary"
        />
        <span>{t("calendar.allDayEvent")}</span>
      </label>

      {canSetEnd && !draft.allDay && (
        <Field label={t("calendar.endTime")}>
          <Input type="time" step={300} value={draft.endTime} onChange={set("endTime")} />
          {timeInvalid && (
            <span className="text-xs text-danger">{t("calendar.endBeforeStart")}</span>
          )}
        </Field>
      )}

      <Field label={t("activity.description")}>
        <Textarea rows={3} value={draft.description} onChange={set("description")} />
      </Field>

      <Field label={t("contact.title")}>
        <Select value={draft.contact_id} onChange={set("contact_id")}>
          <option value="">—</option>
          {contacts.map((c) => (
            <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
          ))}
        </Select>
      </Field>
    </Modal>
  );
}

/** Draft shape shared by the page (create + edit both go through this). */
export function draftFromEvent(event) {
  return {
    id: event.id,
    meetingId: event.meetingId,
    // Kept so a PUT /activities (a full replace) can preserve the fields this
    // form doesn't show -- direction, completed, and the company/deal/project
    // links -- instead of silently nulling them.
    activity: event.activity,
    type: event.type,
    subject: event.subject,
    description: event.description || "",
    contact_id: event.activity.contact_id || "",
    date: toDateInput(event.start),
    startTime: event.allDay ? "09:00" : toLocalInput(event.start).slice(11, 16),
    endTime: event.allDay ? "10:00" : toLocalInput(event.end).slice(11, 16),
    allDay: event.allDay,
  };
}

export function draftForSlot(day, minutes) {
  const allDay = minutes == null;
  const start = minutes == null ? 9 * 60 : minutes;
  const pad = (n) => String(n).padStart(2, "0");
  const hhmm = (m) => `${pad(Math.floor(m / 60) % 24)}:${pad(m % 60)}`;
  return {
    id: null,
    meetingId: null,
    activity: null,
    type: "task",
    subject: "",
    description: "",
    contact_id: "",
    date: toDateInput(day),
    startTime: hhmm(start),
    endTime: hhmm(Math.min(start + 60, 23 * 60 + 59)),
    allDay,
  };
}
